import React, { useState } from 'react';
import { 
  Moon, Smartphone, Briefcase, Users, 
  ArrowRight, Activity, Info 
} from 'lucide-react';

const AuditSlider = ({ label, icon: Icon, value, setValue, min, max, unit, description }) => (
  <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm transition-all hover:border-blue-300">
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
          <Icon size={20} />
        </div>
        <div>
          <h4 className="font-bold text-slate-800 text-sm">{label}</h4>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
      </div>
      <div className="text-xl font-black text-blue-600 font-mono">
        {value} <span className="text-xs text-slate-400 font-bold uppercase">{unit}</span>
      </div>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => setValue(parseInt(e.target.value))}
      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
    />
    <div className="flex justify-between mt-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
      <span>{min} {unit}</span>
      <span>{max} {unit}</span>
    </div>
  </div>
);

export default function ResilienceAudit({ onSubmit }) {
  const [sleep, setSleep] = useState(7);
  const [digital, setDigital] = useState(4);
  const [work, setWork] = useState(8);
  const [social, setSocial] = useState(2);

  const handleSubmit = () => {
    onSubmit({
      sleepHours: sleep,
      phoneHours: digital,
      workHours: work,
      socialCount: social
    });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4">
      
      {/* HEADER */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-widest mb-2">
          <Activity size={14} /> Diagnostic Phase 1
        </div>
        <h2 className="text-3xl font-black text-slate-900">Baseline Capacity Audit</h2>
        <p className="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">
          To build a sustainable schedule, we first need to measure your current "Life Load." 
          <span className="block font-bold text-slate-700 mt-1">Be honest. The simulation only works if the data is real.</span>
        </p>
      </div>

      {/* INPUT GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        {/* 1. SLEEP */}
        <AuditSlider 
          label="Average Sleep" 
          icon={Moon} 
          value={sleep} 
          setValue={setSleep} 
          min={3} max={12} 
          unit="Hrs"
          description="Honest average over the last 7 days."
        />

        {/* 2. DIGITAL LOAD */}
        <AuditSlider 
          label="Digital Consumption" 
          icon={Smartphone} 
          value={digital} 
          setValue={setDigital} 
          min={0} max={16} 
          unit="Hrs"
          description="Screen time (Phone + Social Media)."
        />

        {/* 3. WORK CAPACITY */}
        <AuditSlider 
          label="Work & Study Load" 
          icon={Briefcase} 
          value={work} 
          setValue={setWork} 
          min={0} max={16} 
          unit="Hrs"
          description="Classes, job, and homework combined."
        />

        {/* 4. SOCIAL BATTERY */}
        <AuditSlider 
          label="Social Events" 
          icon={Users} 
          value={social} 
          setValue={setSocial} 
          min={0} max={10} 
          unit="Events"
          description="Hangouts, parties, or clubs per week."
        />

      </div>

      {/* FOOTER ACTION */}
      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex items-start gap-3">
        <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500 leading-relaxed">
          <strong>Privacy Directive:</strong> Your diagnostic data is <strong>anonymized and aggregated by major</strong>. 
          This allows the University to identify which career sectors are facing the highest transition friction. 
          It is never used to track, judge, or report on you as an individual.
        </p>
      </div>

      <button 
        onClick={handleSubmit}
        className="w-full py-4 bg-slate-900 text-white font-black text-lg rounded-2xl shadow-xl hover:bg-black transition-transform active:scale-[0.98] flex items-center justify-center gap-2"
      >
        Analyze My Capacity <ArrowRight size={20} />
      </button>

    </div>
  );
}